import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, TextField, Button, Stack, Chip, Alert,
  CircularProgress, Divider, MenuItem, Autocomplete, IconButton, Tooltip,
  Snackbar, Checkbox,
  FormControlLabel, FormGroup, LinearProgress,
  FormControl, InputLabel, Select,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PetsIcon from '@mui/icons-material/Pets';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { doc, getDoc, collection, query, where, orderBy, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { updateDocument, getCollection } from '../services/firestoreService';
import { useAuth } from '../context/AuthContext';
import {
  syncEgresoHospitalario,
  CONDICIONES_ALTA,
  CAUSAS_MUERTE,
  COMPLICACIONES_OPCIONES,
} from '../services/egresosService';

// ─── Design system ────────────────────────────────────────────────────────────
import { COLORS } from '../theme/constants';

const { primary: PRIMARY, teal: TEAL, danger: DANGER, success: SUCCESS, warning: AMBER } = COLORS;

const toDate = (v) => !v ? null : (typeof v?.toDate === 'function' ? v.toDate() : new Date(v));
const fmtDate = (v) => { const d = toDate(v); return d ? d.toLocaleDateString('es-CL') : '–'; };
const fmtDT   = (v) => { const d = toDate(v); return d ? d.toLocaleString('es-CL')    : '–'; };
const diffDays = (a, b) => { const ms = (toDate(b)||new Date()) - (toDate(a)||new Date()); return Math.max(1, Math.ceil(ms/86400000)); };

// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({ title, color = PRIMARY, children, noPrint = false }) => (
  <Paper elevation={0} sx={{ border:'1px solid #E2E8F0', borderRadius:3, p:2.5, mb:2.5, '@media print': noPrint ? { display:'none' } : {} }}>
    <Typography variant="subtitle1" fontWeight={800} color={color} sx={{ borderBottom:`2px solid ${color}`, pb:0.8, mb:2 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

// ─── Main component ───────────────────────────────────────────────────────────
export default function HospitalEpicrisis() {
  const { hospitalizationId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();

  const [hosp,     setHosp]     = useState(null);
  const [patient,  setPatient]  = useState(null);
  const [diagnoses,setDiagnoses]= useState([]);
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [snackbar, setSnackbar] = useState({ open:false, message:'', severity:'success' });

  // Epicrisis form state
  const [epi, setEpi] = useState({
    diagnosticoDefinitivo: '',
    diagnosticosSecundarios: '',
    diagnosticosDescartados: '',
    resumenEvolucion: '',
    estadoAlAlta: 'mejorado',
    // Discharge condition and complications (feed egresosHospitalarios)
    condicionAlta: '',
    causaMuerte: '',
    complicaciones: false,
    complicacionPostQx: false,
    tiposComplicaciones: [],
    descripcionComplicacion: '',
    // Discharge instructions (owner)
    medicamentosAlAlta: '',
    instruccionesDieta: '',
    instruccionesActividad: '',
    cuidadoHerida: '',
    signosAlarma: '',
    // Follow-up
    controles: '',
    // Technical section
    resumenTecnico: '',
    examenesPendientes: '',
    recomendacionesVet: '',
    // Confirmation
    firmaVeterinario: '',
    firmaVeterinarioId: '',
    consentRecibioInstrucciones: false,
    consentRecibioMedicamentos: false,
    consentSabeControl: false,
    consentSabeAlarma: false,
  });
  const se = (field) => (e) => setEpi(p => ({ ...p, [field]: e.target.value }));
  const be = (field) => (e) => setEpi(p => ({ ...p, [field]: e.target.checked }));

  // ── Load data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'hospitalizations', hospitalizationId));
        if (!snap.exists()) { setLoading(false); return; }
        const h = { id: snap.id, ...snap.data() };
        setHosp(h);

        // Pre-fill epi from existing epicrisis data
        if (h.epicrisis) {
          setEpi(p => ({ ...p, ...h.epicrisis }));
        }

        // Load patient
        if (h.patientId) {
          const pSnap = await getDoc(doc(db, 'patients', h.patientId));
          if (pSnap.exists()) setPatient({ id: pSnap.id, ...pSnap.data() });
        }

        const [diags, usrs] = await Promise.all([getCollection('diagnoses'), getCollection('users')]);
        setDiagnoses(diags);
        setUsers(usrs);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [hospitalizationId]);

  // ── Save epicrisis ────────────────────────────────────────────────────────
  const handleSave = useCallback(async (discharge = false) => {
    setSaving(true);
    const altaAt = new Date();
    const isDeath = epi.condicionAlta === 'Fallecimiento' || epi.condicionAlta === 'Eutanasia';
    try {
      const payload = {
        epicrisis: { ...epi, completadaAt: altaAt, completadaPor: epi.firmaVeterinario, completadaPorId: epi.firmaVeterinarioId },
        updatedAt: altaAt,
      };
      if (discharge) {
        payload.status = 'alta';
        payload.altaAt = altaAt;
        payload.altaDiagnosticoEgreso = epi.diagnosticoDefinitivo;
        payload.limpiezaPendiente = true;
        payload.egresoTipo = isDeath ? 'fallecido' : 'alta';
      }
      await updateDocument('hospitalizations', hospitalizationId, payload);

      if (discharge) {
        await syncEgresoHospitalario({
          hospitalization: { id: hospitalizationId, ...hosp },
          patient,
          epi,
          diagnoses,
          altaAt,
        });
        if (patient?.id) {
          if (isDeath) {
            await updateDocument('patients', patient.id, {
              status: 'fallecido',
              estado_vital: 'FALLECIDO',
              estado_ficha: 'CERRADA',
              fecha_hora_defuncion: altaAt,
              tipo_muerte: epi.condicionAlta === 'Eutanasia' ? 'EUTANASIA' : 'NATURAL_EN_CLINICA',
              lugar: 'CLINICA',
              causa_principal: epi.causaMuerte || '',
              notas_defuncion: epi.resumenEvolucion || '',
              disposicion_restos: 'PENDIENTE',
              veterinario_certificante_id: currentUser?.uid || '',
              veterinario_certificante_name: userData?.name || currentUser?.email || '',
              registrado_por: userData?.name || currentUser?.email || '',
              registrado_por_id: currentUser?.uid || '',
              registrado_en: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });

            // Cancelar citas futuras
            const today = new Date(); today.setHours(0,0,0,0);
            const [b1, b2] = await Promise.all([
              getDocs(query(collection(db, 'onlineBookings'), where('patientId', '==', patient.id))),
              getDocs(query(collection(db, 'onlineBookings'), where('petId', '==', patient.id))),
            ]);
            const bookings = new Map();
            [b1, b2].forEach(snap => snap.docs.forEach(d => bookings.set(d.id, { id: d.id, ...d.data() })));
            for (const b of bookings.values()) {
              const bDate = b.date ? new Date(b.date + 'T00:00:00') : null;
              if (bDate && bDate >= today && b.status !== 'cancelled' && b.status !== 'no_show') {
                await updateDocument('onlineBookings', b.id, {
                  status: 'cancelled',
                  cancellationReason: 'Paciente fallecido',
                  cancelledAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                });
              }
            }
          } else {
            await updateDocument('patients', patient.id, {
              status: 'activo',
              updatedAt: serverTimestamp(),
            });
          }
        }
      }

      setSnackbar({ open: true, message: discharge ? `Alta registrada. Jaula ${hosp?.cageName} pendiente de limpieza.` : 'Epicrisis guardada', severity: 'success' });
      if (discharge) setTimeout(() => navigate('/hospital'), 1500);
    } catch {
      setSnackbar({ open: true, message: 'Error al guardar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [epi, hospitalizationId, hosp, patient, diagnoses, navigate, currentUser, userData]);

  // ── Completeness check ────────────────────────────────────────────────────
  const isDeathDischarge = epi.condicionAlta === 'Fallecimiento' || epi.condicionAlta === 'Eutanasia';
  const checklist = isDeathDischarge
    ? [
        { ok: !!epi.diagnosticoDefinitivo,     label: 'Diagnóstico definitivo' },
        { ok: !!epi.resumenEvolucion,           label: 'Resumen de evolución clínica' },
        { ok: !!epi.condicionAlta,              label: 'Condición de alta' },
        { ok: !!epi.causaMuerte, label: 'Causa de muerte' },
      ]
    : [
        { ok: !!epi.diagnosticoDefinitivo,     label: 'Diagnóstico definitivo' },
        { ok: !!epi.resumenEvolucion,           label: 'Resumen de evolución clínica' },
        { ok: !!epi.medicamentosAlAlta,         label: 'Medicamentos al alta' },
        { ok: !!epi.controles,                  label: 'Controles programados' },
        { ok: !!epi.signosAlarma,               label: 'Signos de alarma' },
        { ok: !!epi.condicionAlta,              label: 'Condición de alta' },
        { ok: !!epi.firmaVeterinario,           label: 'Veterinario firmante' },
      ];
  const requiredOk  = checklist.every(c => c.ok);
  const completePct = Math.round((checklist.filter(c => c.ok).length / checklist.length) * 100);

  if (loading) return <Box sx={{ p:4, display:'flex', justifyContent:'center' }}><CircularProgress/></Box>;
  if (!hosp)   return <Box sx={{ p:4 }}><Alert severity="error">Hospitalización no encontrada</Alert></Box>;

  const dias = diffDays(hosp.admissionAt, hosp.altaAt || new Date());
  const hi   = hosp.hojaIngreso || {};
  const exam = hi.examenFisicoIngreso || {};
  const sis  = exam.sistemasExamen || {};
  const abnormal = Object.entries(sis).filter(([,v]) => !v.normal);

  return (
    <Box sx={{ bgcolor:'#F4F6F8', minHeight:'100vh', pb:6, '@media print': { bgcolor:'white', pb:0 } }}>
      {/* ── Print styles */}
      <style>{`@media print {
        .no-print { display:none!important; }
        body { font-size:12px; }
        /* Expandir textareas multiline para mostrar todo el contenido */
        .MuiInputBase-multiline,
        .MuiOutlinedInput-multiline,
        .MuiInputBase-root:has(textarea),
        .MuiOutlinedInput-root:has(textarea) {
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
          overflow: visible !important;
        }
        .MuiInputBase-multiline textarea,
        .MuiOutlinedInput-multiline textarea,
        .MuiInputBase-inputMultiline,
        .MuiOutlinedInput-inputMultiline,
        textarea {
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
          overflow: visible !important;
          resize: none !important;
        }
      }`}</style>

      {/* ── Header */}
      <Paper elevation={0} sx={{ px:3, py:2, borderBottom:'2px solid #E2E8F0', mb:3, '@media print':{ display:'none' } }} className="no-print">
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={() => navigate(-1)}><ArrowBackIcon/></IconButton>
          <LocalHospitalIcon sx={{ color:PRIMARY }}/>
          <Box sx={{ flex:1 }}>
            <Typography variant="h6" fontWeight={800} color={PRIMARY}>Epicrisis — {hosp.patientName}</Typography>
            <Typography variant="caption" color="text.secondary">{hosp.cageName} · Ingreso: {fmtDate(hosp.admissionAt)} · {dias} días</Typography>
          </Box>
          {/* Completeness bar */}
          <Box sx={{ minWidth:200, textAlign:'right' }}>
            <Typography variant="caption" color={requiredOk ? SUCCESS : AMBER} fontWeight={700}>
              {requiredOk ? '✓ Lista para dar de alta' : `${completePct}% completa`}
            </Typography>
            <LinearProgress variant="determinate" value={completePct}
              sx={{ mt:0.5, height:6, borderRadius:3, bgcolor:'#E2E8F0', '& .MuiLinearProgress-bar':{ bgcolor: requiredOk ? SUCCESS : AMBER } }}/>
          </Box>
          <Tooltip title="Imprimir epicrisis"><IconButton onClick={() => window.print()}><PrintIcon/></IconButton></Tooltip>
          <Button variant="outlined" onClick={() => handleSave(false)} disabled={saving} startIcon={<SaveIcon/>}>Guardar</Button>
          <Button variant="contained" color="error" onClick={() => handleSave(true)} disabled={saving || !requiredOk}
            sx={{ fontWeight:700 }}>
            {saving ? 'Procesando…' : '✓ Confirmar Alta'}
          </Button>
        </Stack>
      </Paper>

      <Box sx={{ px: { xs:2, md:4 }, maxWidth:1000, mx:'auto' }}>

        {/* ── SECCIÓN 1: Encabezado */}
        <Section title="1. Identificación del Caso">
          <Grid container spacing={2}>
            <Grid size={{ xs:12, sm:4 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ width:48, height:48, borderRadius:'50%', bgcolor:'#E2E8F0', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <PetsIcon sx={{ color:'#6B7280' }}/>
                </Box>
                <Box>
                  <Typography fontWeight={800}>{hosp.patientName}</Typography>
                  <Typography variant="caption" color="text.secondary">{hosp.patientSpecies} · {hosp.patientBreed}</Typography>
                </Box>
              </Stack>
            </Grid>
            {[
              ['Propietario', hosp.ownerName],
              ['Veterinario responsable', hosp.veterinarioResponsableNombre || '–'],
              ['Jaula / Canil', hosp.cageName],
              ['Fecha de ingreso', fmtDT(hosp.admissionAt)],
              ['Fecha de alta', hosp.altaAt ? fmtDT(hosp.altaAt) : 'En curso'],
              ['Días de internación', `${dias} días`],
              ['N° de caso', hospitalizationId.slice(0,8).toUpperCase()],
              ['Tipo de ingreso', hosp.tipoIngreso || '–'],
            ].map(([label, val]) => (
              <Grid size={{ xs:12, sm:4 }} key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={600}>{val}</Typography>
              </Grid>
            ))}
          </Grid>
        </Section>

        {/* ── SECCIÓN 2: Resumen del ingreso */}
        <Section title="2. Resumen del Ingreso">
          <Grid container spacing={2}>
            <Grid size={{ xs:12, sm:6 }}>
              <Typography variant="caption" color="text.secondary">Motivo de ingreso</Typography>
              <Typography variant="body2">{hosp.motivoIngreso || '–'}</Typography>
            </Grid>
            <Grid size={{ xs:12, sm:6 }}>
              <Typography variant="caption" color="text.secondary">Diagnóstico presuntivo al ingreso</Typography>
              <Typography variant="body2">{hosp.primaryDiagnosis || '–'}</Typography>
            </Grid>
            <Grid size={{ xs:12, sm:3 }}>
              <Typography variant="caption" color="text.secondary">Estado clínico al ingreso</Typography>
              <Typography variant="body2" fontWeight={600}>{hosp.clinicalStatus || '–'}</Typography>
            </Grid>
            {exam.temperatura && <Grid size={{ xs:6, sm:2 }}><Typography variant="caption" color="text.secondary">T°</Typography><Typography variant="body2">{exam.temperatura}°C</Typography></Grid>}
            {exam.frecuenciaCardiaca && <Grid size={{ xs:6, sm:2 }}><Typography variant="caption" color="text.secondary">FC</Typography><Typography variant="body2">{exam.frecuenciaCardiaca} lpm</Typography></Grid>}
            {exam.spO2 && <Grid size={{ xs:6, sm:2 }}><Typography variant="caption" color="text.secondary">SpO2</Typography><Typography variant="body2">{exam.spO2}%</Typography></Grid>}
            {exam.scoreDolor && <Grid size={{ xs:6, sm:2 }}><Typography variant="caption" color="text.secondary">Dolor</Typography><Typography variant="body2">{exam.scoreDolor}/4</Typography></Grid>}
            {abnormal.length > 0 && (
              <Grid size={{ xs:12 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Sistemas con hallazgos anormales al ingreso:</Typography>
                <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
                  {abnormal.map(([name, v]) => (
                    <Chip key={name} label={`${name}: ${v.notas||'Anormal'}`} size="small" color="warning" sx={{ fontSize:'0.65rem' }}/>
                  ))}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Section>

        {/* ── SECCIÓN 3: Diagnósticos definitivos */}
        <Section title="3. Diagnósticos Definitivos">
          <Grid container spacing={2}>
            <Grid size={{ xs:12 }}>
              <Autocomplete size="small" fullWidth freeSolo id="epi-diagnostico-definitivo" options={diagnoses} getOptionLabel={o => o.diagnosis||''}
                inputValue={epi.diagnosticoDefinitivo}
                onInputChange={(_,v) => setEpi(p=>({...p, diagnosticoDefinitivo:v}))}
                sx={{ width:'100%' }}
                componentsProps={{ listbox: { sx: { '& .MuiAutocomplete-option': { whiteSpace:'normal', wordBreak:'break-word' } } } }}
                renderInput={p => <TextField {...p} fullWidth label="Diagnóstico definitivo principal *" required
                  InputLabelProps={{ ...p.InputLabelProps, shrink: true }}/>}/>
            </Grid>
            <Grid size={{ xs:12, sm:6 }}>
              <TextField size="small" fullWidth label="Diagnósticos secundarios / concomitantes"
                multiline rows={2} value={epi.diagnosticosSecundarios} onChange={se('diagnosticosSecundarios')} InputLabelProps={{ shrink: true }}
                placeholder="Ej: Deshidratación severa, Hipopotasemia"/>
            </Grid>
            <Grid size={{ xs:12, sm:6 }}>
              <TextField size="small" fullWidth label="Diagnósticos descartados (con evidencia)"
                multiline rows={2} value={epi.diagnosticosDescartados} onChange={se('diagnosticosDescartados')} InputLabelProps={{ shrink: true }}
                placeholder="Ej: Obstrucción intestinal (Rx normal)"/>
            </Grid>
          </Grid>
        </Section>

        {/* ── SECCIÓN 4: Condición de Alta y Complicaciones */}
        <Section title="4. Condición de Alta y Complicaciones">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="epi-condicion-alta-label">Condición de alta *</InputLabel>
                <Select
                  size="small"
                  labelId="epi-condicion-alta-label"
                  value={epi.condicionAlta}
                  label="Condición de alta *"
                  onChange={se('condicionAlta')}
                >
                  <MenuItem value="">— Seleccionar —</MenuItem>
                  {CONDICIONES_ALTA.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {(epi.condicionAlta === 'Fallecimiento' || epi.condicionAlta === 'Eutanasia') && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="epi-causa-muerte-label">Causa de muerte / eutanasia</InputLabel>
                  <Select
                    size="small"
                    labelId="epi-causa-muerte-label"
                    value={epi.causaMuerte}
                    label="Causa de muerte / eutanasia"
                    onChange={se('causaMuerte')}
                  >
                    <MenuItem value="">— Seleccionar —</MenuItem>
                    {CAUSAS_MUERTE.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={epi.complicaciones}
                    onChange={(e) => setEpi(p => ({
                      ...p,
                      complicaciones: e.target.checked,
                      tiposComplicaciones: e.target.checked ? p.tiposComplicaciones : [],
                    }))}
                  />
                }
                label="Complicaciones intrahospitalarias"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={epi.complicacionPostQx}
                    onChange={be('complicacionPostQx')}
                  />
                }
                label="Complicación post-quirúrgica"
              />
            </Grid>

            {epi.complicaciones && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Tipos de complicación:
                </Typography>
                <FormGroup row sx={{ flexWrap: 'wrap', gap: 2 }}>
                  {COMPLICACIONES_OPCIONES.map((opt) => (
                    <FormControlLabel
                      key={opt}
                      control={
                        <Checkbox
                          size="small"
                          checked={epi.tiposComplicaciones.includes(opt)}
                          onChange={(e) => {
                            setEpi(p => ({
                              ...p,
                              tiposComplicaciones: e.target.checked
                                ? [...p.tiposComplicaciones, opt]
                                : p.tiposComplicaciones.filter(x => x !== opt),
                            }));
                          }}
                        />
                      }
                      label={opt}
                    />
                  ))}
                </FormGroup>
              </Grid>
            )}

            {(epi.complicaciones || epi.complicacionPostQx) && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Descripción de la complicación"
                  multiline
                  rows={2}
                  value={epi.descripcionComplicacion}
                  onChange={se('descripcionComplicacion')}
                  InputLabelProps={{ shrink: true }}
                  placeholder="Describa brevemente la complicación o evento adverso"
                />
              </Grid>
            )}
          </Grid>
        </Section>

        {/* ── SECCIÓN 5: Evolución clínica */}
        <Section title="5. Evolución Clínica Durante la Internación">
          <Grid container spacing={2}>
            <Grid size={{ xs:12, sm:4 }}>
              <TextField size="small" fullWidth select id="epi-estado-alta" label="Estado clínico al alta" value={epi.estadoAlAlta} onChange={se('estadoAlAlta')} InputLabelProps={{ shrink: true }}>
                {[['estable','Estable'],['mejorado','Mejorado'],['recuperado','Recuperado/Curado'],['cronico','Crónico estabilizado'],['paliativo','Paliativo'],['fallecido','Fallecido']].map(([k,l])=>
                  <MenuItem key={k} value={k}>{l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs:12 }}>
              <TextField size="small" fullWidth label="Resumen narrativo de la evolución clínica *" multiline rows={5}
                value={epi.resumenEvolucion} onChange={se('resumenEvolucion')} InputLabelProps={{ shrink: true }}
                placeholder="Describir evolución cronológica: respuesta al tratamiento, complicaciones, momentos de inflexión, estado al alta…"
                required/>
            </Grid>
          </Grid>
        </Section>

        {!isDeathDischarge && (
        <>
        {/* ── SECCIÓN 6: Instrucciones de alta (propietario) */}
        <Section title="6. Instrucciones de Alta para el Propietario" color={TEAL}>
          <Grid container spacing={2}>
            <Grid size={{ xs:12 }}>
              <TextField size="small" fullWidth label="Medicamentos para la casa *" multiline rows={4}
                value={epi.medicamentosAlAlta} onChange={se('medicamentosAlAlta')} InputLabelProps={{ shrink: true }}
                placeholder="Ej: Amoxicilina 500mg — 1 comprimido 2 veces al día por 7 días, con comida. Metronidazol 250mg jarabe — 3ml 2 veces al día por 5 días…"
                required/>
            </Grid>
            <Grid size={{ xs:12, sm:6 }}>
              <TextField size="small" fullWidth label="Instrucciones de dieta" multiline rows={3}
                value={epi.instruccionesDieta} onChange={se('instruccionesDieta')} InputLabelProps={{ shrink: true }}
                placeholder="Ej: Dieta blanda por 5 días: arroz con pollo hervido, 3 veces al día, porción pequeña…"/>
            </Grid>
            <Grid size={{ xs:12, sm:6 }}>
              <TextField size="small" fullWidth label="Restricciones de actividad" multiline rows={3}
                value={epi.instruccionesActividad} onChange={se('instruccionesActividad')} InputLabelProps={{ shrink: true }}
                placeholder="Ej: Reposo absoluto 48h, luego reposo relativo 5 días (sin saltos ni juego brusco)…"/>
            </Grid>
            <Grid size={{ xs:12, sm:6 }}>
              <TextField size="small" fullWidth label="Cuidado de herida / suturas (si aplica)" multiline rows={3}
                value={epi.cuidadoHerida} onChange={se('cuidadoHerida')} InputLabelProps={{ shrink: true }}
                placeholder="Ej: Limpiar herida con suero fisiológico 2 veces al día. Retiro de suturas en 10 días…"/>
            </Grid>
            <Grid size={{ xs:12, sm:6 }}>
              <TextField size="small" fullWidth label="⚠ Signos de alarma — cuándo consultar urgencias *" multiline rows={3}
                value={epi.signosAlarma} onChange={se('signosAlarma')} InputLabelProps={{ shrink: true }}
                placeholder="Ej: Fiebre > 39.5°C, vómitos repetidos, decaimiento marcado, dificultad para respirar, herida con pus o abierta…"
                required/>
            </Grid>
          </Grid>
        </Section>
        </>
        )}



        {!isDeathDischarge && (
        <>
        {/* ── SECCIÓN 7: Controles programados */}
        <Section title="7. Seguimiento y Próximos Controles">
          <TextField size="small" fullWidth label="Controles programados *" multiline rows={3}
            value={epi.controles} onChange={se('controles')} InputLabelProps={{ shrink: true }}
            placeholder="Ej: Control de herida 7 días (15/Jul). Retiro suturas 10 días (18/Jul). Control laboratorio 30 días…"
            required/>
        </Section>
        </>
        )}

        {/* ── SECCIÓN 8: Para el veterinario referidor (técnica) */}
        <Section title="8. Sección Técnica (para veterinario tratante / referidor)">
          <Grid container spacing={2}>
            <Grid size={{ xs:12 }}>
              <TextField size="small" fullWidth label="Resumen clínico técnico" multiline rows={4}
                value={epi.resumenTecnico} onChange={se('resumenTecnico')} InputLabelProps={{ shrink: true }}
                placeholder="Resumen en lenguaje técnico con diagnósticos, plan terapéutico, evolución y pronóstico…"/>
            </Grid>
            <Grid size={{ xs:12, sm:6 }}>
              <TextField size="small" fullWidth label="Exámenes pendientes al alta" multiline rows={2}
                value={epi.examenesPendientes} onChange={se('examenesPendientes')} InputLabelProps={{ shrink: true }}
                placeholder="Ej: Cultivo de orina — resultado esperado en 5 días, enviar a info@clinica.cl"/>
            </Grid>
            <Grid size={{ xs:12, sm:6 }}>
              <TextField size="small" fullWidth label="Recomendaciones para el médico de cabecera" multiline rows={2}
                value={epi.recomendacionesVet} onChange={se('recomendacionesVet')} InputLabelProps={{ shrink: true }}
                placeholder="Ej: Dieta renal permanente, ecocardiograma anual, control BUN/creatinina mensual los primeros 3 meses…"/>
            </Grid>
          </Grid>
        </Section>

        {!isDeathDischarge && (
        <>
        {/* ── SECCIÓN 9: Firmas y conformidad */}
        <Section title="9. Confirmación del Alta y Firmas">
          <Grid container spacing={2}>
            <Grid size={{ xs:12, sm:6 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>CONFORMIDAD DEL PROPIETARIO</Typography>
              <FormGroup>
                {[
                  ['consentRecibioInstrucciones', 'He recibido las instrucciones de alta y las comprendo'],
                  ['consentRecibioMedicamentos',  'He recibido los medicamentos indicados'],
                  ['consentSabeControl',          'Tengo claro cuándo debo regresar a control'],
                  ['consentSabeAlarma',           'Sé qué signos de alarma debo vigilar'],
                ].map(([field, label]) => (
                  <FormControlLabel key={field}
                    control={<Checkbox size="small" checked={epi[field]} onChange={be(field)}/>}
                    label={<Typography variant="caption">{label}</Typography>}/>
                ))}
              </FormGroup>
            </Grid>
            <Grid size={{ xs:12, sm:6 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>FIRMADO POR</Typography>
              <Autocomplete size="small" fullWidth id="epi-firma-veterinario" options={users.filter(u=>['Médico Veterinario','Director Médico'].includes(u.role))}
                getOptionLabel={u=>`${u.name} ${u.lastName||''}`.trim()}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                value={users.find(u => `${u.name} ${u.lastName||''}`.trim() === epi.firmaVeterinario) || null}
                onChange={(_, v) => {
                  if (v) {
                    setEpi(p => ({ ...p, firmaVeterinario: `${v.name} ${v.lastName||''}`.trim(), firmaVeterinarioId: v.id }));
                  } else {
                    setEpi(p => ({ ...p, firmaVeterinario: '', firmaVeterinarioId: '' }));
                  }
                }}
                inputValue={epi.firmaVeterinario}
                onInputChange={(_, v) => setEpi(p => ({ ...p, firmaVeterinario: v, firmaVeterinarioId: p.firmaVeterinarioId }))}
                freeSolo
                componentsProps={{ listbox: { sx: { '& .MuiAutocomplete-option': { whiteSpace:'normal', wordBreak:'break-word' } } } }}
                renderInput={p=><TextField {...p} fullWidth label="Veterinario firmante *" required
                  InputLabelProps={{ ...p.InputLabelProps, shrink: true }}/>}
                sx={{ width:'100%', mb:1.5 }}/>
              <Typography variant="caption" color="text.secondary">Fecha: {new Date().toLocaleDateString('es-CL')}</Typography>
            </Grid>
          </Grid>
        </Section>
        </>
        )}

        {/* ── Completeness checklist */}
        <Paper elevation={0} sx={{ border:`2px solid ${requiredOk ? SUCCESS : AMBER}`, borderRadius:3, p:2, mb:3 }} className="no-print">
          <Typography variant="caption" fontWeight={700} color={requiredOk ? SUCCESS : AMBER} display="block" mb={1}>
            {requiredOk ? '✓ Epicrisis completa — lista para dar de alta' : 'Campos requeridos para dar de alta:'}
          </Typography>
          <Stack direction="row" flexWrap="wrap" spacing={1}>
            {checklist.map(c => (
              <Chip key={c.label} size="small" icon={c.ok ? <CheckCircleIcon/> : <WarningIcon/>}
                label={c.label} color={c.ok ? 'success' : 'warning'}
                sx={{ fontSize:'0.65rem', fontWeight:700 }}/>
            ))}
          </Stack>
        </Paper>

        {/* ── Bottom actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" className="no-print" sx={{ mb:4 }}>
          <Button onClick={() => navigate(-1)} variant="outlined">Volver</Button>
          <Button onClick={() => window.print()} startIcon={<PrintIcon/>} variant="outlined">Imprimir Epicrisis</Button>
          <Button onClick={() => handleSave(false)} disabled={saving} startIcon={<SaveIcon/>} variant="contained" sx={{ bgcolor: PRIMARY }}>
            {saving ? 'Guardando…' : 'Guardar borrador'}
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving || !requiredOk} variant="contained" color="error" sx={{ fontWeight:700 }}>
            {saving ? 'Procesando…' : '✓ Confirmar Alta definitiva'}
          </Button>
        </Stack>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar(p=>({...p,open:false}))}
        anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p=>({...p,open:false}))} sx={{ width:'100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
